'use client';

import * as React from 'react';
import { useLocale, useTranslations } from 'next-intl';
import * as LucideIcons from 'lucide-react';
import { Shield } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { getLocalizedPath } from '@/lib/locale-utils';

interface IndustrySolution {
    id: string;
    title: string;
    description: string;
    icon: string;
}

interface IndustrySolutionsGridProps {
    limit?: number;
    className?: string;
    showOnHomePageOnly?: boolean;
    /** SSG: pre-fetched solutions (skip client fetch when provided) */
    initialSolutions?: IndustrySolution[];
}

// Helper function to get icon component dynamically from lucide-react
const getIconComponent = (iconName?: string): React.ComponentType<{ className?: string }> => {
    if (!iconName) return Shield;
    const IconComponent = (LucideIcons as any)[iconName] as React.ComponentType<{ className?: string }> | undefined;
    return IconComponent || Shield;
};

const iconPalette = [
    { light: 'text-primary-600', dark: 'text-primary-400' },
    { light: 'text-emerald-600', dark: 'text-emerald-400' },
    { light: 'text-amber-600', dark: 'text-amber-400' },
    { light: 'text-sky-600', dark: 'text-sky-400' },
    { light: 'text-rose-600', dark: 'text-rose-400' },
    { light: 'text-indigo-600', dark: 'text-indigo-400' },
];

export function IndustrySolutionsGrid({ limit, className, showOnHomePageOnly = false, initialSolutions }: IndustrySolutionsGridProps) {
    const locale = useLocale();
    const t = useTranslations('home.industrySolutions');
    const [solutions, setSolutions] = React.useState<IndustrySolution[]>(() => (initialSolutions?.length ? (limit ? initialSolutions.slice(0, limit) : initialSolutions) : []));
    const [isLoading, setIsLoading] = React.useState(!initialSolutions?.length);

    React.useEffect(() => {
        if (initialSolutions?.length) {
            setSolutions(limit ? initialSolutions.slice(0, limit) : initialSolutions);
            setIsLoading(false);
            return;
        }
        const fetchIndustrySolutions = async () => {
            try {
                setIsLoading(true);
                const url = showOnHomePageOnly
                    ? `/api/industry-solutions?locale=${locale}&showOnHomePage=true`
                    : `/api/industry-solutions?locale=${locale}`;
                const response = await fetch(url, {
                    cache: 'no-store',
                });
                if (response.ok) {
                    const data = await response.json();
                    const fetchedSolutions = Array.isArray(data) ? data : [];
                    setSolutions(limit ? fetchedSolutions.slice(0, limit) : fetchedSolutions);
                } else {
                    fallbackToStatic();
                }
            } catch (error) {
                fallbackToStatic();
            } finally {
                setIsLoading(false);
            }
        };

        const fallbackToStatic = () => {
            try {
                const rawIndustries = t.raw('industries');
                const staticIndustries = (Array.isArray(rawIndustries)
                    ? (rawIndustries as { title: string; description: string; href: string }[])
                    : []
                )
                    .slice(0, limit || 6)
                    .map((industry, index) => {
                        const icons = ['Shield', 'Building2', 'DollarSign', 'Bitcoin', 'Heart', 'Sparkles'];
                        return {
                            id: `fallback-${index}`,
                            title: industry.title,
                            description: industry.description,
                            icon: icons[index] ?? 'Shield',
                            href: industry.href,
                        };
                    });
                setSolutions(staticIndustries);
            } catch (fallbackError) {
                setSolutions([]);
            }
        };

        fetchIndustrySolutions();
    }, [limit, showOnHomePageOnly, locale, t, initialSolutions?.length]);

    if (isLoading) {
        return (
            <div className={`grid gap-6 md:grid-cols-2 lg:grid-cols-3 ${className || ''}`}>
                {[...Array(limit || 6)].map((_, index) => (
                    <div
                        key={`skeleton-${index}`}
                        className="h-64 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"
                    />
                ))}
            </div>
        );
    }

    if (solutions.length === 0) {
        return (
            <div className={`text-center py-12 ${className || ''}`}>
                <p className="text-gray-500 dark:text-gray-400 text-lg">
                    {t('emptyMessage')}
                </p>
            </div>
        );
    }

    return (
        <div className={`grid gap-6 md:grid-cols-2 lg:grid-cols-3 ${className || ''}`}>
            {solutions.map((solution, index) => {
                const IconComponent = getIconComponent(solution.icon);
                const palette = iconPalette[index % iconPalette.length];
                const ColoredIcon = (props: React.SVGProps<SVGSVGElement>) => (
                    <IconComponent
                        {...props}
                        className={`${props.className ?? ''} ${palette.light} dark:${palette.dark}`}
                    />
                );
                // Use a default href or get from solution if available
                const href = (solution as any).href 
                    ? getLocalizedPath((solution as any).href, locale)
                    : getLocalizedPath('services/mlm-software-development', locale);

                return (
                    <Card
                        key={solution.id}
                        title={solution.title}
                        description={solution.description}
                        href={href}
                        icon={ColoredIcon}
                        index={index}
                        showIndex={false}
                        showFooterLink={false}
                        animationDelay={index * 80}
                    />
                );
            })}
        </div>
    );
}

